import { dataToField, fieldDto, isChoiceFieldType } from "@salestrak/custom-fields";
import {
  Box,
  Card,
  CardPanel,
  Checkbox,
  Collapsible,
  CollapsiblePanel,
  EmptyState,
  Field,
  FieldDescription,
  FieldError,
  FieldItem,
  FieldLabel,
  Icon,
  LoadingState,
  Progress,
  Radio,
  RadioGroup,
  Text,
  toastManager,
} from "@salestrak/ui";
import { Fragment, memo, useRef, useState, type KeyboardEvent, type MouseEvent, type ReactNode } from "react";

import {
  CustomFieldRenderer,
} from "#app/components/custom-fields/custom-field-renderers";
import { getOptionMetas } from "#app/components/custom-fields/option-visuals";
import { FormBanner, hasFormBanner } from "#app/components/forms/form-banner";
import type { CustomFieldDef } from "#app/components/custom-fields/types";
import { FormPhotosAnswer } from "#app/components/forms/form-fill/form-photos-answer";
import {
  FormOptionLabel,
  formOptionProperty,
  useFormOptionEntity,
} from "#app/components/forms/form-property-options";
import {
  type Form,
  type FormProperty,
  type FormFieldItem,
  type FormItem,
  type FormStep,
  formVisibility,
  isFieldItem,
  isFillableItem,
  isPrefilledAnswer,
  resolveItemField,
  submissionLimitOf,
  type SubmissionErrors,
} from "#app/lib/forms";
import { countOf } from "#app/lib/utils/pluralize";

const FORM_FILL_MAX_WIDTH = 640;

export function FormFillColumn({ children }: { children: ReactNode }) {
  return (
    <Box direction="column" fill="width" gap="md" marginX="auto" maxWidth={FORM_FILL_MAX_WIDTH}>
      {children}
    </Box>
  );
}

export const FORM_SUBMIT_MS = 1200;

export function FormTitleCard({
  form,
  children,
}: {
  form: Pick<Form, "name" | "description" | "bannerUrl" | "bannerMediaId">;
  children?: ReactNode;
}) {
  return (
    <FormFillCard
      accent
      cover={
        hasFormBanner(form) ? <FormBanner alt={`${form.name} banner`} form={form} /> : undefined
      }
    >
      <Box direction="column" gap="2xs">
        <Text size="xl" weight="semibold">
          {form.name}
        </Text>
        {form.description ? (
          <Text pre size="sm" variant="muted">
            {form.description}
          </Text>
        ) : null}
        {children}
      </Box>
    </FormFillCard>
  );
}

export function cantSubmitToast(held: readonly FormItem[], found: SubmissionErrors): void {
  const [first] = held;
  if (!first) return;
  toastManager.add({
    description:
      held.length === 1
        ? `"${first.label}" — ${found[first.itemId]}`
        : `${countOf(held.length, "question")} still need an answer.`,
    title: "Can't submit yet",
    type: "error",
  });
}

export function FormSubmitOutcome({
  phase,
  form,
  submitted,
  actions,
}: {
  phase: "submitting" | "submitted";
  form: Pick<Form, "settings">;
  submitted: number;
  actions?: ReactNode;
}) {
  if (phase === "submitting") return <LoadingState caption="Submitting…" />;
  const limit = submissionLimitOf(form);
  const lines = [
    formVisibility(form) === "public"
      ? "Anyone who can see this Project can open it."
      : "Only you, people it's shared with, and admins can open it.",
    limit === null
      ? null
      : submitted < limit
        ? `${submitted} of ${limit} submitted on this Project.`
        : limit === 1
          ? "This Project takes one submission."
          : `That's all ${limit} this Project takes.`,
  ].filter((line): line is string => line !== null);
  return (
    <Box align="center" direction="column" duration={100} enter="fade" fill="flex" justify="center">
      <EmptyState
        actions={actions}
        description={lines.join(" ")}
        icon="check-circle"
        media="icon"
        title="Form submitted"
      />
    </Box>
  );
}

export function FormFillBody({
  form,
  answers,
  errors,
  hidden,
  customFieldsById,
  onAnswerChange,
  prefilled,
  revisions,
}: {
  form: Pick<Form, "items">;
  answers: Record<string, unknown>;
  errors: SubmissionErrors;
  /** A typed answer reads its value once, so a new revision remounts it. */
  revisions?: ReadonlyMap<string, number> | undefined;
  prefilled?: Record<string, unknown> | undefined;
  /** A hidden item stays mounted and collapses in place; a reveal never scrolls the page. */
  hidden?: ReadonlySet<string>;
  customFieldsById: ReadonlyMap<string, CustomFieldDef>;
  onAnswerChange: (itemId: string, dtoValue: unknown) => void;
}) {
  const items = form.items.filter((item) => isFillableItem(item, customFieldsById));

  const asked = items.filter((item) => isFieldItem(item) && !hidden?.has(item.itemId));
  const nextAsked = (itemId: string) =>
    asked[asked.findIndex((item) => item.itemId === itemId) + 1];

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const target = event.target;
    if (event.key !== "Enter" || event.nativeEvent.isComposing) return;
    if (!(target instanceof HTMLInputElement) || !target.enterKeyHint) return;
    event.preventDefault();
    const card = target.closest(`[${FORM_ITEM_ATTR}]`);
    const next = card && nextAsked(card.getAttribute(FORM_ITEM_ATTR) ?? "");
    const control = next && firstControlIn(document.getElementById(formItemDomId(next.itemId)));
    if (control) control.focus();
    else target.blur();
  };

  return (
    <FormFillStack onKeyDown={onKeyDown}>
      {items.map((item) => {
        const card = (
          <FormFillCard itemId={item.itemId}>
            <FormFillItem
              customFieldsById={customFieldsById}
              enterKeyHint={nextAsked(item.itemId) ? "next" : "done"}
              key={revisions?.get(item.itemId) ?? 0}
              error={errors[item.itemId]}
              fromProject={isPrefilledAnswer(prefilled, item.itemId, answers[item.itemId])}
              item={item}
              onAnswerChange={onAnswerChange}
              value={answers[item.itemId]}
            />
          </FormFillCard>
        );
        // Root is display: contents so a collapsed panel leaves no gap slot in the column.
        if (item.kind === "section" || !item.showWhen) {
          return <Fragment key={item.itemId}>{card}</Fragment>;
        }
        return (
          <Collapsible
            key={item.itemId}
            open={!hidden?.has(item.itemId)}
            render={<Box display="contents" />}
          >
            {/* The card's drop reaches ~14px below it in dark mode; the
                panel clips at its content's edge, so it leaves the stack's
                gap worth of room for it — the one card that can collapse
                must not be the one card without a shadow. */}
            <CollapsiblePanel bleed={FORM_FILL_STACK_GAP} stackGap={FORM_FILL_STACK_GAP}>
              {card}
            </CollapsiblePanel>
          </Collapsible>
        );
      })}
    </FormFillStack>
  );
}

const FORM_FILL_STACK_GAP = "md";

export function FormFillStack({
  children,
  onKeyDown,
}: {
  children: ReactNode;
  onKeyDown?: (event: KeyboardEvent<HTMLDivElement>) => void;
}) {
  return (
    <Box direction="column" gap={FORM_FILL_STACK_GAP} fill="width" onKeyDown={onKeyDown}>
      {children}
    </Box>
  );
}

export function formItemDomId(itemId: string): string {
  return `form-item-${itemId}`;
}

const FORM_ITEM_ATTR = "data-form-item";

function firstControlIn(card: HTMLElement | null): HTMLElement | null {
  if (!card) return null;
  const controls = card.querySelectorAll<HTMLElement>(
    'input, textarea, button, [contenteditable="true"], [role="radio"], [role="checkbox"]',
  );
  return (
    Array.from(controls).find(
      (control) =>
        control.tabIndex >= 0 &&
        !control.hasAttribute("disabled") &&
        control.getAttribute("aria-hidden") !== "true" &&
        !(control instanceof HTMLInputElement && control.type === "hidden"),
    ) ?? null
  );
}

export function FormFillCard({
  accent = false,
  cover,
  itemId,
  children,
}: {
  accent?: boolean;
  cover?: ReactNode;
  itemId?: string;
  children: ReactNode;
}) {
  return (
    <Card
      density="regular"
      fill="width"
      surface="elevated"
      {...(itemId ? { id: formItemDomId(itemId), [FORM_ITEM_ATTR]: itemId } : {})}
    >
      {cover ?? (accent ? <Box background="primary" height={6} /> : null)}
      <CardPanel>{children}</CardPanel>
    </Card>
  );
}

export function FormStepRail({
  done,
  left,
}: {
  done: number;
  left: number;
}) {
  const reached = (done / Math.max(done + left, 1)) * 100;
  const [held, setHeld] = useState({ done, value: reached });
  const value = done < held.done ? reached : Math.max(reached, held.value);
  if (held.done !== done || held.value !== value) setHeld({ done, value });
  return (
    <Box width={96}>
      <Progress aria-label="Form progress" value={value} />
    </Box>
  );
}

export function FormFillReview({
  pages,
  errors,
  hidden,
  customFieldsById,
  onJump,
}: {
  pages: readonly FormStep[];
  errors: SubmissionErrors;
  hidden: ReadonlySet<string>;
  customFieldsById: ReadonlyMap<string, CustomFieldDef>;
  onJump: (itemId: string) => void;
}) {
  const groups = pages.flatMap((page) => {
    const rows = page.items.filter(
      (item): item is FormFieldItem =>
        isFieldItem(item) && !hidden.has(item.itemId) && item.itemId in errors,
    );
    return rows.length > 0 ? [{ rows, section: page.section }] : [];
  });
  const count = groups.reduce((sum, group) => sum + group.rows.length, 0);
  return (
    <FormFillCard>
      <Box direction="column" gap="md">
        <Box align="start" direction="row" gap="sm">
          <Icon
            color={count > 0 ? "warning" : "success"}
            name={count > 0 ? "alert-triangle" : "check-circle"}
            size="lg"
          />
          <Box direction="column" gap="2xs">
            <Text size="lg" weight="semibold">
              {count > 0
                ? `${countOf(count, "question")} still ${count === 1 ? "needs" : "need"} an answer`
                : "Nothing missed"}
            </Text>
            <Text size="sm" variant="muted">
              {count > 0
                ? "Press a question to go to it. Submit once they're answered."
                : "Every required question is answered. Submit when you're ready."}
            </Text>
          </Box>
        </Box>
        {groups.map((group) => (
          <Box direction="column" gap="2xs" key={group.section?.itemId ?? "start"}>
            {group.section ? (
              <Box paddingX="sm">
                <Text size="xs" variant="faint" weight="medium">
                  {group.section.label}
                </Text>
              </Box>
            ) : null}
            {group.rows.map((item) => (
              <Box
                align="center"
                as="button"
                cursor="pointer"
                direction="row"
                fill="width"
                gap="base"
                hoverBackground
                key={item.itemId}
                minHeight={36}
                onClick={() => onJump(item.itemId)}
                paddingX="sm"
                paddingY="xs"
                pressFeedback
                rounded="md"
                type="button"
              >
                <Box direction="column" grow minWidth={0}>
                  <Text align="left" size="sm" truncate weight="medium">
                    {item.label || resolveItemField(item, customFieldsById)?.name}
                  </Text>
                  <Text align="left" size="xs" variant="destructive">
                    {errors[item.itemId]}
                  </Text>
                </Box>
                <Icon color="faint" name="chevron-right" size="sm" />
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    </FormFillCard>
  );
}

export function FormSectionHeading({
  item,
  label,
}: {
  item: Extract<FormItem, { kind: "section" }>;
  label?: ReactNode;
}) {
  return (
    <Box direction="column" gap="2xs">
      {label ?? (
        <Text size="lg" weight="semibold">
          {item.label}
        </Text>
      )}
      {item.description ? (
        <Text size="sm" variant="muted">
          {item.description}
        </Text>
      ) : null}
    </Box>
  );
}

export const FormFillItem = memo(function FormFillItem({
  item,
  value,
  error,
  customFieldsById,
  onAnswerChange,
  label,
  inertAnswer = false,
  enterKeyHint,
  fromProject = false,
}: {
  item: FormItem;
  value: unknown;
  error: string | undefined;
  fromProject?: boolean;
  customFieldsById: ReadonlyMap<string, CustomFieldDef>;
  onAnswerChange: (itemId: string, dtoValue: unknown) => void;
  enterKeyHint?: "next" | "done";
  label?: ReactNode;
  inertAnswer?: boolean;
}) {
  if (item.kind === "section") return <FormSectionHeading item={item} label={label} />;
  if (item.kind === "text") {
    return (
      <Box direction="column" gap="2xs">
        {item.label ? (
          <Text size="sm" weight="semibold">
            {item.label}
          </Text>
        ) : null}
        {item.body ? (
          <Text pre size="sm">
            {item.body}
          </Text>
        ) : null}
      </Box>
    );
  }
  if (!isFieldItem(item)) return null;

  const field = resolveItemField(item, customFieldsById);
  if (!field) {
    return (
      <Field fill gap="base">
        <Box direction="column" fill={label === undefined ? undefined : "width"}>
          <FieldLabel required={label === undefined && item.required} size="sm">
            {label ?? item.label}
          </FieldLabel>
        </Box>
        <Text size="sm" variant="faint">
          This question is no longer available.
        </Text>
      </Field>
    );
  }

  const invalid = error !== undefined;
  const isChoice = isChoiceFieldType(field.type);
  const property = formOptionProperty(item);
  const answer =
    field.type === "photos" ? (
      <FormPhotosAnswer
        field={field}
        itemId={item.itemId}
        label={item.label || field.name}
        value={value}
      />
    ) : isChoice ? (
      <ChoiceAnswer
        deselectable={!item.required}
        field={field}
        onChange={(values) => onAnswerChange(item.itemId, fieldDto(field.type, values))}
        property={property}
        value={value}
      />
    ) : field.type === "checkbox" ? (
      <CheckboxAnswer
        onChange={(checked) => onAnswerChange(item.itemId, fieldDto(field.type, checked))}
        value={dataToField(field.type, value).fieldValue}
      />
    ) : (
      <Box fill="width">
        <CustomFieldRenderer
          enterKeyHint={enterKeyHint}
          field={field}
          formAnswer
          invalid={invalid}
          onChangeValue={(formValue) =>
            onAnswerChange(item.itemId, fieldDto(field.type, formValue))
          }
          surface="default"
          value={dataToField(field.type, value).fieldValue}
        />
      </Box>
    );
  return (
    <Field fill gap="base" invalid={invalid}>
      {/* The Field hugs its rows; the canvas's stand-in name stretches so
          its rename can take the card's width. A filler's label keeps
          hugging — a wider label is a wider press that focuses the answer. */}
      <Box direction="column" fill={label === undefined ? undefined : "width"} gap="2xs">
        <FieldLabel required={label === undefined && item.required} size="sm">
          {label ?? (item.label || field.name)}
        </FieldLabel>
        {item.description ? <FieldDescription>{item.description}</FieldDescription> : null}
      </Box>
      {inertAnswer ? (
        <Box direction="column" fill="width" inert>
          {answer}
        </Box>
      ) : (
        answer
      )}
      {invalid ? (
        <FieldError match>{error}</FieldError>
      ) : fromProject ? (
        <Text size="xs" variant="faint">
          From Project
        </Text>
      ) : null}
    </Field>
  );
});

function ChoiceAnswer({
  field,
  value,
  onChange,
  property,
  deselectable,
}: {
  field: CustomFieldDef;
  deselectable: boolean;
  value: unknown;
  onChange: (values: string[]) => void;
  property: FormProperty | null;
}) {
  const options = getOptionMetas(field);
  const entityOf = useFormOptionEntity(property);
  const selected = Array.isArray(value) ? value.map(String) : [];
  if (options.length === 0) {
    return (
      <Text size="sm" variant="faint">
        No choices available
      </Text>
    );
  }
  if (field.type === "select") {
    return (
      <SingleChoiceRows
        deselectable={deselectable}
        onChange={(next) => onChange(next == null ? [] : [next])}
        options={options.map((option) => ({
          label: <FormOptionLabel entity={entityOf(option.value)} label={option.label} />,
          value: option.value,
        }))}
        value={selected[0] ?? null}
      />
    );
  }
  return (
    <ChoiceList>
      {options.map((option) => {
        const checked = selected.includes(option.value);
        return (
          <ChoiceRow
            key={option.value}
            label={<FormOptionLabel entity={entityOf(option.value)} label={option.label} />}
          >
            <Checkbox
              checked={checked}
              onCheckedChange={(nextChecked) =>
                onChange(
                  nextChecked
                    ? [...selected, option.value]
                    : selected.filter((v) => v !== option.value),
                )
              }
            />
          </ChoiceRow>
        );
      })}
    </ChoiceList>
  );
}

function CheckboxAnswer({
  value,
  onChange,
}: {
  value: unknown;
  onChange: (checked: boolean) => void;
}) {
  return (
    <ChoiceList>
      <ChoiceRow label={<Text size="sm">Yes</Text>}>
        <Checkbox checked={value === true} onCheckedChange={(checked) => onChange(checked)} />
      </ChoiceRow>
    </ChoiceList>
  );
}

function SingleChoiceRows({
  options,
  value,
  onChange,
  deselectable,
}: {
  options: readonly { value: string; label: ReactNode }[];
  value: string | null;
  onChange: (value: string | null) => void;
  deselectable: boolean;
}) {
  const pressedChosen = useRef(false);
  return (
    <RadioGroup
      value={value}
      onValueChange={(next) => onChange(next == null ? null : String(next))}
    >
      <ChoiceList>
        {options.map((option) => (
          <ChoiceRow
            key={option.value}
            label={option.label}
            onClick={
              deselectable
                ? (event) => {
                    if (!pressedChosen.current) return;
                    pressedChosen.current = false;
                    // Stop the label handing the press to the radio, which would pick the row straight back.
                    event.preventDefault();
                    onChange(null);
                  }
                : undefined
            }
            onPointerDown={
              deselectable
                ? () => {
                    pressedChosen.current = value === option.value;
                  }
                : undefined
            }
          >
            <Radio value={option.value} />
          </ChoiceRow>
        ))}
      </ChoiceList>
    </RadioGroup>
  );
}

function ChoiceList({ children }: { children: ReactNode }) {
  return (
    <Box direction="column" fill="width">
      {children}
    </Box>
  );
}

/** Each row is its own `FieldItem`, else every option is named by the question for screen readers. */
function ChoiceRow({
  label,
  children,
  onClick,
  onPointerDown,
}: {
  label: ReactNode;
  children: ReactNode;
  onClick?: ((event: MouseEvent<HTMLElement>) => void) | undefined;
  onPointerDown?: (() => void) | undefined;
}) {
  return (
    <FieldItem>
      <Box
        align="center"
        as="label"
        cursor="pointer"
        direction="row"
        fill="width"
        gap="base"
        hoverBackground
        minHeight={{ base: 44, sm: 36 }}
        onClick={onClick}
        onPointerDown={onPointerDown}
        paddingX="sm"
        pressFeedback
        rounded="md"
      >
        {children}
        {label}
      </Box>
    </FieldItem>
  );
}
